// Per-tab admission control. Foreground reads and writes never wait on this queue.
export function createBackgroundRequestQueue(limit = 2, canStart = () => true) {
  const pending = new Map();
  let active = 0;
  const canceled = () => Object.assign(new Error('Request canceled'), { code: 'ERR_CANCELED' });
  function start(entry) {
    entry.started = true;
    active++;
    Promise.resolve().then(() => {
      if (entry.controller.signal.aborted) throw canceled();
      return entry.work(entry.controller.signal);
    }).then(value => entry.finish(null, value), error => entry.finish(error)).finally(() => {
      active--;
      if (pending.get(entry.key) === entry) pending.delete(entry.key);
      drain();
    });
  }
  function drain() {
    if (!canStart()) return;
    for (const entry of pending.values()) {
      if (active >= limit) break;
      if (entry.started) continue;
      start(entry);
    }
  }
  return {
    resume: drain,
    run(key, work, signal) {
      if (signal?.aborted) return Promise.reject(canceled());
      let entry = pending.get(key);
      if (!entry) {
        entry = { key, work, controller: new AbortController(), consumers: new Set(), started: false };
        entry.finish = (error, value) => {
          // Remove before notifying consumers, so a subsequent read starts fresh.
          if (pending.get(key) === entry) pending.delete(key);
          for (const consumer of entry.consumers) consumer.finish(error, value);
          entry.consumers.clear();
        };
        pending.set(key, entry);
      }
      const promise = new Promise((resolve, reject) => {
        const consumer = { finish(error, value) {
          signal?.removeEventListener('abort', abort);
          if (error) reject(error); else resolve(value);
        } };
        const abort = () => {
          entry.consumers.delete(consumer);
          consumer.finish(canceled());
          if (!entry.consumers.size) {
            entry.controller.abort();
            if (pending.get(key) === entry) pending.delete(key);
            drain();
          }
        };
        entry.consumers.add(consumer);
        signal?.addEventListener('abort', abort, { once: true });
      });
      drain();
      return promise;
    },
  };
}
